import React, { useCallback, useEffect, useState } from 'react';
import { Flex } from '@chakra-ui/layout';
import { BookingParams } from '../../types/BookingTypes';
import { SingleObject } from '../../types/ObjectTypes';
import { ObjectParams } from '../../types/ParamsTypes';
import { fetchObject } from '../../services/Object/ObjectService';
import {
  getInitialBookingParams,
  getInitialBookingRouteParams,
} from '../../services/Booking/BookingService';
import { BookingConfirmation } from '../../components/Booking/BookingConfirmation';
import { BookingTravelDataForm } from '../../components/Booking/BookingTravelForm';
import { BookingPersonalDetailsForm } from '../../components/Booking/BookingPersonalDetailsForm';
import { ObjectOverview } from '../../components/Booking/ObjectOverview/ObjectOverview';
import { ExtendedDetailsRoute } from '../../types/RoutesTypes';
import { useParams } from 'react-router-dom';

export interface BookingViewProps {
  handleLoadingSpinner: (value: boolean) => void;
  isLoading: boolean;
}

export const BookingView = (props: BookingViewProps): JSX.Element => {
  const { isLoading, handleLoadingSpinner } = props;
  const routeParams: ExtendedDetailsRoute = useParams();
  const [bookingParams, setBookingParams] = useState<BookingParams>();
  const [object, setObject] = useState<SingleObject>();
  const [currentStep, setCurrentStep] = useState<number>(1);

  const handleObjectParamsChange = useCallback(
    (objectParams: ObjectParams, bookingParams: BookingParams) => {
      (async function() {
        const objectDetailData = await fetchObject(objectParams);

        setBookingParams(bookingParams);
        setObject(objectDetailData.data.data.result.bookings);
      })();
    },
    [],
  );

  const handleBookingParamsChange = useCallback(
    (bookingParams: BookingParams) => {
      setBookingParams(bookingParams);
    },
    [bookingParams],
  );

  const handleStepChange = useCallback(
    value => {
      setCurrentStep(value);
    },
    [currentStep, setCurrentStep],
  );

  const handleRedirectToObject = useCallback(() => {
    getInitialBookingRouteParams(routeParams);
  }, [routeParams]);

  useEffect(() => {
    (async function() {
      const bookingRouteParams = getInitialBookingRouteParams(routeParams);
      const objectDetailData = await fetchObject(bookingRouteParams);
      setObject(objectDetailData.data.data.result.bookings);
      setBookingParams(
        getInitialBookingParams(objectDetailData.data.data.result.bookings, bookingRouteParams),
      );
      handleLoadingSpinner(false);
    })();
  }, [handleLoadingSpinner]);

  return (
    <Flex
      gridGap={6}
      alignItems={'flex-start'}
      flexDirection={{ base: 'column-reverse', xl: 'row' }}>
      {!isLoading && (
        <>
          {currentStep === 3 && bookingParams && (
            <BookingConfirmation bookingParams={bookingParams} object={object as SingleObject} />
          )}
          {(currentStep === 1 || currentStep === 2) && (
            <>
              <Flex w={'100%'}>
                {currentStep === 1 && object && bookingParams && (
                  <BookingTravelDataForm
                    currentStep={currentStep}
                    handleStepChange={handleStepChange}
                    handleObjectParamsChange={handleObjectParamsChange}
                    object={object}
                    bookingParams={bookingParams}
                  />
                )}

                {currentStep == 2 && object && bookingParams && (
                  <BookingPersonalDetailsForm
                    currentStep={currentStep}
                    handleStepChange={handleStepChange}
                    handleBookingParamsChange={handleBookingParamsChange}
                    object={object}
                    bookingParams={bookingParams}
                  />
                )}
              </Flex>
              {object && bookingParams && (
                <Flex width={{ sm: '100%', md: '100%', lg: '100%', xl: 'auto' }}>
                  <ObjectOverview object={object} bookingParams={bookingParams} />
                </Flex>
              )}
            </>
          )}
        </>
      )}
    </Flex>
  );
};
